import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import { CircularProgress, Grid, Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { transformChartData, getUnitForPower } from '../../utils/apiCommonMethods';
import { AppChartCard } from '../@dashboard/app';
import { fetchSiteStatusChartData } from '../../utils/chartAPI';

SiteStatusCharts.propTypes = {
  site: PropTypes.object.isRequired,
  startDate: PropTypes.object,               
  endDate: PropTypes.object,               
  msSessionInfo: PropTypes.object.isRequired,
  refreshKey: PropTypes.number,
};

export default function SiteStatusCharts({
  site,
  startDate,
  endDate,
  msSessionInfo,
  refreshKey,
}) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const [seriesPower, setSeriesPower] = useState([]);
  const [seriesSoc, setSeriesSoc] = useState([]);
  const [seriesPoi, setSeriesPoi] = useState([]);

  const [hasAnyPower, setHasAnyPower] = useState(false);
  const [hasAnySoc, setHasAnySoc] = useState(false);
  const [hasAnyPoi, setHasAnyPoi] = useState(false);

  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedChart, setExpandedChart] = useState(null);

  const handleExpandContract = (key) => {
    setExpandedChart((prev) => (prev === key ? null : key));
    setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
  };

  // helper: robust timestamp convert
  const toMs = (t) => (typeof t === 'number' ? t : dayjs(t).valueOf());

  // helper: pair labels + values -> [{x,y}]
  const pairXY = (labelsArr = [], valuesArr = []) =>
    labelsArr.map((lbl, i) => ({ x: toMs(lbl), y: valuesArr[i] }));

  useEffect(() => {
    const fetchChartData = async () => {
      setLoading(true);
      try {
        const data =
          (await fetchSiteStatusChartData(site, startDate, endDate, msSessionInfo)) ??
          {};
        setChartData({ tsStatus: data });
      } finally {
        setLoading(false);
      }
    };

    if (site?.id && msSessionInfo && inView && startDate && endDate) {
      fetchChartData();
    } else if (inView) {
      
      setLoading(false);
      setChartData(null);
      setSeriesPower([]);
      setSeriesSoc([]);
      setSeriesPoi([]);
      setHasAnyPower(false);
      setHasAnySoc(false);
      setHasAnyPoi(false);
    }
  }, [
    site?.id,
    startDate?.valueOf?.(),
    endDate?.valueOf?.(),
    msSessionInfo,
    refreshKey,
    inView,
  ]);

  useEffect(() => {
    if (!chartData) return;

    const clean = transformChartData(chartData);

    // POWER
    if (clean?.power?.labels?.length && clean?.power?.dataSet?.length) {
      setSeriesPower([
        {
          name: 'Power',
          type: 'area',
          data: pairXY(clean.power.labels, clean.power.dataSet),
        },
      ]);
      setHasAnyPower(true);
    } else {
      setSeriesPower([]);
      setHasAnyPower(false);
    }

    // SOC
    if (clean?.soc?.labels?.length && clean?.soc?.dataSet?.length) {
      setSeriesSoc([
        {
          name: 'SOC %',
          type: 'area',
          data: pairXY(clean.soc.labels, clean.soc.dataSet),
        },
      ]);
      setHasAnySoc(true);
    } else {
      setSeriesSoc([]);
      setHasAnySoc(false);
    }

    const hasPoiLabels = clean?.pvToGrid?.labels?.length;
    const poiSeries = [];
    if (hasPoiLabels) {
      if (clean?.pvToGrid?.dataSet?.length) {
        poiSeries.push({
          name: 'Grid Output',
          type: 'area',
          data: pairXY(clean.pvToGrid.labels, clean.pvToGrid.dataSet),
        });
      }
      if (clean?.bessImport?.dataSet?.length) {
        poiSeries.push({
          name: 'Battery Charge',
          type: 'area',
          data: pairXY(clean.pvToGrid.labels, clean.bessImport.dataSet),
        });
      }
      if (clean?.bessExport?.dataSet?.length) {
        poiSeries.push({
          name: 'Battery Discharge',
          type: 'area',
          data: pairXY(clean.pvToGrid.labels, clean.bessExport.dataSet),
        });
      }
    }

    setSeriesPoi(poiSeries);
    setHasAnyPoi(poiSeries.length > 0);
  }, [chartData]);


  if (!inView || loading) {
    return (
      <div
        ref={ref}
        style={{ minHeight: 320, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (!hasAnyPower && !hasAnySoc && !hasAnyPoi) {
    return (
      <Box ref={ref} sx={{ minHeight: 320, textAlign: 'center', pt: 5 }}>
        <Typography variant="body2">No chart data available for this site.</Typography>
      </Box>
    );
  }

  const hasPoi = hasAnyPoi;

  return (
    <Box sx={{ mb: hasPoi ? 0 : (theme) => `-${theme.spacing(3)}` }}>
      {/* ─── Battery Power + SOC ─── */}
      {(hasAnyPower || hasAnySoc) && (
        <Grid container spacing={3} alignItems="stretch" ref={ref}>
          {hasAnyPower && (
            <Grid
              item
              xs={12}
              md={expandedChart === 'power' ? 12 : 6}
              style={{ display: expandedChart === 'soc' ? 'none' : 'block' }}
            >
              <AppChartCard
                sx={{ height: '100%' }}
                title="Battery Power"
                subheader={getUnitForPower(site?.id)}
                chartData={seriesPower}                 
                xMinMs={startDate?.valueOf()}           
                xMaxMs={endDate?.valueOf()}
                expandContractChart={() => handleExpandContract('power')}
              />
            </Grid>
          )}

          {hasAnySoc && (
            <Grid
              item
              xs={12}
              md={expandedChart === 'soc' ? 12 : 6}
              style={{ display: expandedChart === 'power' ? 'none' : 'block' }}
            >
              <AppChartCard
                sx={{ height: '100%' }}
                title="State of Charge"
                subheader="SOC %"
                chartData={seriesSoc}                  
                xMinMs={startDate?.valueOf()}
                xMaxMs={endDate?.valueOf()}
                expandContractChart={() => handleExpandContract('soc')}
                forceYMinZero
              />
            </Grid>
          )}
        </Grid>
      )}

      {/* ─── POI row (renders only if data exists) ─── */}
      {hasPoi && (
        <Grid container spacing={3} mt={0.1}>
          <Grid item xs={12}>
            <AppChartCard
              title="Grid Output"
              subheader="kW"
              chartData={seriesPoi}                    
              xMinMs={startDate?.valueOf()}
              xMaxMs={endDate?.valueOf()}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}